import { useState, Fragment, useEffect, useRef } from 'react'
import { SimpleInput } from '../simple-input'
import { Button } from '../trustyle-button'
import SimpleDropDown from '../simple-drop-down'
import isDeepEqual from 'fast-deep-equal/react'
import InputAlert from '../input-alert'

const AddressFormComponent = ({
  id,
  label,
  lookup,
  onChange,
  error,
  formVariant,
  hasTimeAtAddress,
}) => {
  const [formValue, Form] = useAddressForm(
    {
      county: '',
      number: '',
      buildingname: '',
      subbuildingname: '',
      street: '',
      posttown: '',
      postcode: '',
      summaryline: '',
      yearsAtAddress: '0',
    },
    id,
    label,
    lookup,
    error,
    formVariant,
    hasTimeAtAddress,
  )
  const formRef = useRef(formValue)

  if (!isDeepEqual(formRef.current, formValue)) {
    formRef.current = formValue
  }

  useEffect(() => {
    onChange(id, { ...formValue })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formRef.current])

  return <Fragment>{Form}</Fragment>
}

const useAddressForm = (
  defaultState,
  index,
  label,
  lookup,
  error,
  formVariant,
  hasTimeAtAddress,
) => {
  const [state, setState] = useState(defaultState)
  return [
    state,
    <AddressLookup
      data={state}
      label={label}
      id={index}
      lookup={lookup}
      setData={setState}
      defaultState={defaultState}
      error={error}
      formVariant={formVariant}
      hasTimeAtAddress={hasTimeAtAddress}
    />,
    setState,
  ]
}

const AddressLookup = ({
  setData,
  data,
  id,
  label,
  lookup,
  defaultState,
  error,
  formVariant,
  hasTimeAtAddress,
}) => {
  const variantPath = element => `elements.form.variants.${formVariant}.${element}`
  const [postcode, setPostcode] = useState('')
  const [addresses, setAddresses] = useState([])
  // postcode | static | edit
  const [addressView, setAddressView] = useState('postcode')

  const returnData = (key, returnValue) => {
    setData(prevData => ({
      ...prevData,
      [key]: returnValue,
    }))
  }

  const handleOnChange = e => {
    if (e.target.value !== 'not-listed') {
      // Spread so we keep years at the address
      setData({
        ...data,
        ...addresses.find(a => a.summaryline === e.target.value),
      })
      setAddressView('static')
    } else {
      setAddressView('edit')
    }
  }

  const handleViewChange = view => {
    if (view === 'postcode') {
      setData(defaultState)
      setAddresses([])
    }

    setAddressView(view)
  }

  const postCodeInput = useRef(null)
  const postcodeError =
    error &&
    ((data.premise === '' && data.number === '') || data.posttown === '' || data.postcode === '')
  const postcodeView = (
    <div>
      <label htmlFor='postcode' sx={{ variant: 'elements.form.address-lookup.label' }}>
        Please enter your postcode
      </label>
      <div
        sx={{
          variant: 'elements.form.address-lookup.postcode-row',
          '@media (min-width: 810px)': {
            display: 'flex',
            flexDirection: 'row',
          },
        }}
      >
        <div>
          <SimpleInput
            defaultValue={postcode}
            ref={postCodeInput}
            name={'postcode'}
            hasError={postcodeError}
          />
        </div>
        <Button
          sx={{
            marginTop: 'sm',
            '@media (min-width: 810px)': {
              width: 'auto',
              marginTop: 0,
              marginLeft: 16,
            },
          }}
          onClick={() => {
            const postCode = postCodeInput.current.value
            setPostcode(postCode)
            lookup(postCodeInput.current.value, setAddresses)
          }}
          size={'small'}
          variant={'secondary'}
        >
          Find address
        </Button>
      </div>

      <If condition={addresses.length > 0}>
        <div sx={{ mt: 'sm' }}>
          <SimpleDropDown
            name={'addresslist'}
            placeholder={'Please select your address'}
            options={[
              { label: 'Please select your address' },
              { label: "My address isn't listed", value: 'not-listed' },
              ...addresses.map(address => {
                return { label: address.summaryline, value: address.summaryline }
              }),
            ]}
            onChange={handleOnChange}
            onBlur={() => {}}
            value={null}
            hasError={postcodeError}
          />
        </div>
      </If>

      <If condition={postcodeError}>
        <InputAlert type='alert' sx={{ pl: '0', pr: '0', variant: variantPath('input.error') }}>
          Please select your address
        </InputAlert>
      </If>
    </div>
  )

  const staticView = (
    <div>
      <button
        type='button'
        sx={{ variant: 'elements.form.address-lookup.button' }}
        onClick={e => handleViewChange('postcode')}
      >
        Back to address finder
      </button>
      <div sx={{ mt: 'sm', variant: 'elements.form.address-lookup.display' }}>
        <div>{data.subbuildingname}</div>
        <div>{data.buildingname}</div>
        <div>
          {data.number} {data.street}
        </div>
        <div>{data.posttown}</div>
        <div>{data.county}</div>
        <div>{data.postcode}</div>
        <button
          type='button'
          sx={{ variant: 'elements.form.address-lookup.button' }}
          onClick={e => handleViewChange('edit')}
        >
          Edit address
        </button>
      </div>
    </div>
  )

  const editView = (
    <Fragment>
      <button
        type='button'
        sx={{ variant: 'elements.form.address-lookup.button' }}
        onClick={e => handleViewChange('postcode')}
      >
        Back to address finder
      </button>
      <div sx={{ mt: 'sm', variant: 'elements.form.address-lookup.edit-container' }}>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='number' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Building number
          </label>
          <SimpleInput
            name={'number'}
            onChange={e => returnData('number', e.currentTarget.value)}
            value={data.number}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='buildingname' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Building name
          </label>
          <SimpleInput
            name={'buildingname'}
            onChange={e => returnData('buildingname', e.currentTarget.value)}
            value={data.buildingname}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='subbuildingname' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Flat number
          </label>
          <SimpleInput
            name={'subbuildingname'}
            onChange={e => returnData('subbuildingname', e.currentTarget.value)}
            value={data.subbuildingname}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='street' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Street
          </label>
          <SimpleInput
            name={'street'}
            onChange={e => returnData('street', e.currentTarget.value)}
            value={data.street}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='posttown' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Town or city
          </label>
          <SimpleInput
            name={'posttown'}
            onChange={e => returnData('posttown', e.currentTarget.value)}
            value={data.posttown}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='county' sx={{ variant: 'elements.form.address-lookup.label' }}>
            County
          </label>
          <SimpleInput
            name={'county'}
            onChange={e => returnData('county', e.currentTarget.value)}
            value={data.county}
          />
        </div>
        <div sx={{ variant: 'elements.form.address-lookup.input-container' }}>
          <label htmlFor='postcode' sx={{ variant: 'elements.form.address-lookup.label' }}>
            Postcode
          </label>
          <SimpleInput
            name={'postcode'}
            onChange={e => returnData('postcode', e.currentTarget.value)}
            value={data.postcode}
          />
        </div>
        <input type='hidden' value={data.uprn} />
      </div>
    </Fragment>
  )

  const TimeAtAddress = () => (
    <div>
      <label htmlFor='postcode' sx={{ mt: 'sm', variant: 'elements.form.address-lookup.label' }}>
        Time at this address
      </label>
      <SimpleDropDown
        name={'yearslist'}
        placeholder={'Please select'}
        options={[
          { label: 'Please select', value: 0 },
          ...[...Array(16).keys()].map(i => {
            const index = i + 1
            if (index === 1) {
              return { label: '1 year or less', value: index }
            } else if (index === 16) {
              return { label: '+16 years', value: index }
            } else {
              return { label: `${index} years`, value: index }
            }
          }),
        ]}
        onChange={e => returnData('yearsAtAddress', e.currentTarget.value)}
        onBlur={() => {}}
        value={data.yearsAtAddress}
        hasError={error && (data.yearsAtAddress === '' || data.yearsAtAddress === '0')}
      />

      <If condition={error && (data.yearsAtAddress === '' || data.yearsAtAddress === '0')}>
        <InputAlert type='alert' sx={{ pl: '0', pr: '0', variant: variantPath('input.error') }}>
          Please select how many years you've lived at this address
        </InputAlert>
      </If>
    </div>
  )

  return (
    <div sx={{ variant: 'elements.form.address-lookup.container' }}>
      <If condition={label}>
        <h2>{label}</h2>
      </If>
      {addressView === 'postcode' ? postcodeView : addressView === 'static' ? staticView : editView}
      <If condition={hasTimeAtAddress}>
        <TimeAtAddress />
      </If>
    </div>
  )
}

export default AddressFormComponent
